import React from "react";
import { api, mediaUrl } from "@/lib/admin/api";
import { PageHead } from "@/pages/admin/Layout";
import { TextInput, TextArea, SelectInput, toast, isVideoUrl } from "@/components/admin/ui";
import { useLiveSiteBaseUrl, liveIndexUrl } from "@/lib/admin/liveSite";
import { SmartMedia } from "@/components/admin/SmartMedia";

/* ====================================================================
   2High · Owner Cabinet — Testimonials / Proof Manager (Feb 2026)
   --------------------------------------------------------------------
   Same operator-grade pattern as Products:
     LEFT  · proof list with 60px thumbnails, kind pill, visibility,
              toggle / duplicate+open / edit
     RIGHT · slide-out edit panel
              §A Basic · §B Media · §C Live preview
              footer: Cancel · Save & Next · Save Proof
              after save: also shows "View on Live Site"
   ==================================================================== */

const TYPES = [
  { id: "customer_review_video",  label: "Customer Review Video", short: "review video", accept: "video" },
  { id: "customer_review_image",  label: "Customer Review Image", short: "review image", accept: "image" },
  { id: "landed_photo",           label: "Landed Package Photo",  short: "landed",       accept: "image" },
  { id: "telegram_screenshot",    label: "Telegram Screenshot",   short: "telegram",     accept: "image" },
];
const TYPE_LABEL = Object.fromEntries(TYPES.map((t) => [t.id, t.label]));
const TYPE_SHORT = Object.fromEntries(TYPES.map((t) => [t.id, t.short]));

const BLANK = {
  kind: "customer_review_image",
  title: "",
  caption: "",
  name: "",
  region: "",
  img: "",
  video_poster: "",
  visible: true,
};

function uid() { return Math.random().toString(36).slice(2, 10); }

function loadItem(it) {
  return {
    ...BLANK,
    id: it.id,
    kind: it.kind || "customer_review_image",
    title: it.title || "",
    caption: it.caption || it.quote || "",
    name: it.name || "",
    region: it.region || it.city || "",
    img: it.img || "",
    video_poster: it.video_poster || "",
    visible: it.visible !== false,
    created_at: it.created_at,
    updated_at: it.updated_at,
  };
}

function exportItem(form) {
  return {
    kind: form.kind,
    title: form.title,
    caption: form.caption,
    name: form.name || "",
    region: form.region || "",
    img: form.img,
    video_poster: form.video_poster || "",
    visible: !!form.visible,
  };
}

/* ============================== PAGE ============================== */
export default function Proof() {
  const [items, setItems] = React.useState([]);
  const [search, setSearch] = React.useState("");
  const [kindFilter, setKindFilter] = React.useState("all");
  const [showHidden, setShowHidden] = React.useState(false);

  const [editingId, setEditingId] = React.useState(null);
  const [form, setForm] = React.useState(BLANK);
  const [saving, setSaving] = React.useState(false);
  const [lastSavedId, setLastSavedId] = React.useState(null);

  const reload = React.useCallback(async () => {
    setItems(await api.listTestimonials(false));
  }, []);
  React.useEffect(() => { reload(); }, [reload]);

  const filtered = React.useMemo(() => {
    const q = search.trim().toLowerCase();
    return items
      .filter((it) => (showHidden ? true : it.visible !== false))
      .filter((it) => kindFilter === "all" || (it.kind || "customer_review_image") === kindFilter)
      .filter((it) => {
        if (!q) return true;
        return [it.title, it.name, it.caption, it.quote].filter(Boolean).join(" ").toLowerCase().includes(q);
      });
  }, [items, search, kindFilter, showHidden]);

  function openNew() {
    setEditingId("new");
    setForm({ ...BLANK });
    setLastSavedId(null);
  }
  function openEdit(it) {
    setEditingId(it.id);
    setForm(loadItem(it));
    setLastSavedId(null);
  }
  function closeEditor() {
    setEditingId(null);
    setForm(BLANK);
    setLastSavedId(null);
  }

  async function duplicateAndOpen(it) {
    // Testimonials API has no duplicate helper — clone client-side via createTestimonial
    try {
      const payload = exportItem(loadItem(it));
      payload.title = `${payload.title || "(untitled)"} · copy`;
      payload.visible = false;
      const dup = await api.createTestimonial(payload);
      toast("Duplicated · editing copy", "ok");
      await reload();
      openEdit(dup);
    } catch (e) {
      toast(e.response?.data?.detail || "Duplicate failed", "error");
    }
  }

  async function toggleVisible(it) {
    try {
      await api.updateTestimonial(it.id, { visible: !(it.visible !== false) });
      toast(it.visible === false ? "Now visible on site" : "Hidden from site", "ok");
      reload();
    } catch (e) { toast("Update failed", "error"); }
  }

  async function deleteItem(it) {
    if (!window.confirm(`Delete "${it.title || "this item"}" permanently?\n\nIt will be removed from the public site immediately.`)) return;
    try {
      await api.deleteTestimonial(it.id);
      toast("Deleted", "ok");
      if (editingId === it.id) closeEditor();
      reload();
    } catch (e) { toast("Delete failed", "error"); }
  }

  async function bulkHide() {
    const visible = filtered.filter((p) => p.visible !== false);
    if (!visible.length) return;
    if (!window.confirm(`Hide ${visible.length} visible items from the site?`)) return;
    await Promise.all(visible.map((p) => api.updateTestimonial(p.id, { visible: false })));
    toast(`${visible.length} hidden`, "ok");
    reload();
  }

  async function save({ thenNext = false } = {}) {
    if (!form.img?.trim()) return toast("Media required", "error");
    setSaving(true);
    try {
      const payload = exportItem(form);
      let res;
      if (editingId === "new") res = await api.createTestimonial(payload);
      else res = await api.updateTestimonial(editingId, payload);
      setLastSavedId(res.id);
      toast(editingId === "new" ? "Proof added" : "Saved", "ok");
      await reload();
      if (thenNext) {
        setEditingId("new");
        setForm({ ...BLANK, kind: form.kind });   // keep type for fast batch
        setLastSavedId(null);
      } else if (editingId === "new") {
        setEditingId(res.id);
        setForm(loadItem(res));
      }
    } catch (e) {
      toast(e.response?.data?.detail || "Save failed", "error");
    } finally {
      setSaving(false);
    }
  }

  /* ============================== RENDER ============================== */
  return (
    <div data-testid="proof-page" className="-mx-4 lg:-mx-8 -my-4 lg:-my-8 min-h-screen px-4 lg:px-8 py-4 lg:py-8">
      <PageHead
        eye={`Proof wall · ${items.length} items · 4 types`}
        title="Testimonials / Proof"
        sub="Customer videos, review clips, landed packages, Telegram screenshots."
        right={
          <div className="flex items-center gap-2 flex-wrap">
            <button
              data-testid="proof-bulk-hide"
              onClick={bulkHide}
              className="inline-flex items-center gap-2 px-3.5 py-2.5 rounded-lg border border-[#c9a86b]/25 bg-white/[.03] hover:border-[#c9a86b] hover:bg-[#c9a86b]/[.06] text-[12px] tracking-[.18em] uppercase text-[#e8e4d8]/85 transition"
            >Bulk Hide</button>
            <button
              data-testid="proof-add-new"
              onClick={openNew}
              className="inline-flex items-center gap-2 px-4 py-2.5 rounded-lg text-[12px] tracking-[.18em] uppercase font-bold transition"
              style={{ background: "linear-gradient(180deg,#d4b675,#a07c39)", color: "#1a1407" }}
            >
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
              Add New Proof
            </button>
          </div>
        }
      />

      {/* Toolbar */}
      <div className="mb-5 grid grid-cols-1 md:grid-cols-[1fr_220px_auto] gap-2 p-2.5 rounded-xl bg-[#13150f] border border-[#22261b]">
        <div className="relative">
          <svg className="absolute left-3 top-3 opacity-60" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.6"><path d="M21 21l-4.3-4.3M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16z"/></svg>
          <input
            data-testid="proof-search"
            value={search} onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by title, caption, member…"
            className="w-full pl-10 pr-3 py-2.5 rounded-lg bg-[#0e110a] border border-[#22261b] text-[13px] focus:outline-none focus:border-[#c9a86b]"
          />
        </div>
        <select
          data-testid="proof-filter-kind"
          value={kindFilter} onChange={(e) => setKindFilter(e.target.value)}
          className="px-3 py-2.5 rounded-lg bg-[#0e110a] border border-[#22261b] text-[13px] focus:outline-none focus:border-[#c9a86b]"
        >
          <option value="all">All types ({items.length})</option>
          {TYPES.map((t) => {
            const n = items.filter((it) => (it.kind || "customer_review_image") === t.id).length;
            return <option key={t.id} value={t.id}>{t.label} ({n})</option>;
          })}
        </select>
        <button
          data-testid="proof-show-hidden"
          onClick={() => setShowHidden((v) => !v)}
          className={`px-3 py-2.5 rounded-lg text-[11px] tracking-[.2em] uppercase border transition ${
            showHidden ? "bg-[#c9a86b] text-[#1a1407] border-[#c9a86b]" : "bg-[#0e110a] text-[#e8e4d8]/65 border-[#22261b] hover:border-[#c9a86b]"
          }`}
        >{showHidden ? "Showing Hidden" : "Hidden Off"}</button>
      </div>

      {/* Layout */}
      <div className={`grid gap-5 ${editingId ? "grid-cols-1 xl:grid-cols-[1fr_640px]" : "grid-cols-1"}`}>

        {/* LIST */}
        <section data-testid="proof-list" className="rounded-2xl border border-[#22261b] bg-[#13150f] overflow-hidden">
          <div className="grid grid-cols-[68px_1.4fr_0.9fr_0.6fr_180px] gap-3 items-center px-4 py-3 text-[10px] tracking-[.22em] uppercase text-[#6e6f5f] bg-[#10120c] border-b border-[#22261b]">
            <div></div>
            <div>Title</div>
            <div>Type</div>
            <div>Visible</div>
            <div className="text-right">Actions</div>
          </div>

          {filtered.length === 0 ? (
            <div className="p-12 text-center text-[#6e6f5f] text-[13px]">
              {items.length === 0
                ? <>No proof posted yet. Click <span className="text-[#c9a86b]">Add New Proof</span> to start.</>
                : "Nothing matches these filters."}
            </div>
          ) : filtered.map((it) => (
            <ProofRow
              key={it.id}
              it={it}
              selected={editingId === it.id}
              onEdit={() => openEdit(it)}
              onDuplicate={() => duplicateAndOpen(it)}
              onToggle={() => toggleVisible(it)}
              onDelete={() => deleteItem(it)}
            />
          ))}
        </section>

        {/* EDIT PANEL */}
        {editingId && (
          <EditPanel
            key={editingId}
            isNew={editingId === "new"}
            form={form}
            setForm={setForm}
            saving={saving}
            lastSavedId={lastSavedId}
            onClose={closeEditor}
            onSave={() => save({ thenNext: false })}
            onSaveAndNext={() => save({ thenNext: true })}
          />
        )}
      </div>
    </div>
  );
}

/* ============================== ROW ============================== */
function ProofRow({ it, selected, onEdit, onDuplicate, onToggle, onDelete }) {
  const visible = it.visible !== false;
  const kind = it.kind || "customer_review_image";
  const isVideo = isVideoUrl(it.img);
  const thumb = it.img ? (isVideo ? (it.video_poster ? mediaUrl(it.video_poster) : null) : mediaUrl(it.img)) : null;

  return (
    <div
      data-testid={`proof-row-${it.id}`}
      onClick={onEdit}
      className={`grid grid-cols-[68px_1.4fr_0.9fr_0.6fr_180px] gap-3 items-center px-4 py-3 border-b border-[#22261b] cursor-pointer transition ${
        selected ? "bg-[#181c12] shadow-[inset_3px_0_0_#c9a86b]" : "hover:bg-[#10130c]"
      } ${!visible ? "opacity-50" : ""}`}
    >
      <div
        className="w-14 h-14 rounded-lg border border-[#2c3122] flex-shrink-0 relative overflow-hidden bg-[#0a0c08]"
      >
        {thumb ? (
          <RowThumb src={thumb} alt={it.title || "proof"} />
        ) : (
          <div className="w-full h-full grid place-items-center text-[#6e6f5f] text-[10px]">no img</div>
        )}
        {isVideo && (
          <div className="absolute inset-0 grid place-items-center text-white text-[18px] z-10" style={{ textShadow: "0 1px 6px #000" }}>▶</div>
        )}
      </div>
      <div className="min-w-0">
        <div className="text-[14px] font-semibold truncate">{it.title || it.name || <span className="text-[#6e6f5f]">(untitled)</span>}</div>
        <div className="text-[11px] text-[#a9aa9a] mt-0.5 line-clamp-1">{it.caption || it.quote || ""}</div>
      </div>
      <div>
        <span className="inline-flex items-center px-2 py-1 rounded-md text-[10.5px] bg-[#1a1e13] text-[#bbc09e] border border-[#2c3122] font-mono tracking-[.04em]">
          {TYPE_SHORT[kind] || kind}
        </span>
      </div>
      <div className={`text-[11.5px] font-semibold ${visible ? "text-[#a8d39d]" : "text-[#6e6f5f]"}`}>
        {visible ? "● Live" : "○ Hidden"}
      </div>
      <div className="flex items-center justify-end gap-1.5" onClick={(e) => e.stopPropagation()}>
        <button
          data-testid={`proof-toggle-${it.id}`}
          onClick={onToggle}
          title={visible ? "Hide from site" : "Show on site"}
          className={`relative w-9 h-5 rounded-full border transition ${visible ? "bg-[#2d3a23] border-[#5fa15a]" : "bg-[#1f2218] border-[#2c3122]"}`}
        >
          <span className={`absolute top-[1px] w-3.5 h-3.5 rounded-full transition-all ${visible ? "left-[18px] bg-[#5fa15a]" : "left-[2px] bg-[#6e6f5f]"}`} />
        </button>
        <button
          data-testid={`proof-duplicate-${it.id}`}
          onClick={onDuplicate}
          title="Duplicate + open"
          className="w-8 h-8 rounded-md border border-[#2c3122] grid place-items-center text-[#a9aa9a] hover:border-[#c9a86b] hover:text-[#c9a86b] transition"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="9" width="11" height="11" rx="1.5"/><path d="M5 15V5a1 1 0 0 1 1-1h10"/></svg>
        </button>
        <button
          data-testid={`proof-edit-${it.id}`}
          onClick={onEdit}
          title="Edit"
          className={`w-8 h-8 rounded-md border grid place-items-center transition ${
            selected ? "border-[#c9a86b] text-[#c9a86b] bg-[#231d10]" : "border-[#2c3122] text-[#a9aa9a] hover:border-[#c9a86b] hover:text-[#c9a86b]"
          }`}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4v16h16v-7M18.5 2.5a2.1 2.1 0 1 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
        </button>
        <button
          data-testid={`proof-delete-${it.id}`}
          onClick={onDelete}
          title="Delete proof"
          className="w-8 h-8 rounded-md border border-red-500/30 grid place-items-center text-red-400 hover:bg-red-500/15 hover:border-red-500/60 transition"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" strokeLinejoin="round"><path d="M3 6h18"/><path d="M8 6V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/></svg>
        </button>
      </div>
    </div>
  );
}

/* ============================== EDIT PANEL ============================== */
function EditPanel({ isNew, form, setForm, saving, lastSavedId, onClose, onSave, onSaveAndNext }) {
  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const kindMeta = TYPES.find((t) => t.id === form.kind) || TYPES[1];
  const liveBase = useLiveSiteBaseUrl();
  // landed_photo lives in #landedRail, all other proof kinds live in #trust
  const anchor = form.kind === "landed_photo" ? "landed" : "trust";
  const liveSiteHref = liveIndexUrl(liveBase, anchor);

  return (
    <aside data-testid="proof-edit-panel" className="rounded-2xl border border-[#22261b] bg-[#13150f] overflow-hidden flex flex-col xl:sticky xl:top-6 xl:max-h-[calc(100vh-3rem)]">
      <div className="flex items-start justify-between px-5 py-4 border-b border-[#22261b] bg-[#10120c]">
        <div>
          <h2 className="text-[16px] font-bold leading-tight">{isNew ? "New Proof" : `Edit · ${form.title || "(untitled)"}`}</h2>
          <div className="text-[10.5px] tracking-[.08em] font-mono text-[#6e6f5f] mt-1">
            {isNew ? "Will be created on save" : `${kindMeta.short}`}
          </div>
        </div>
        <button
          data-testid="proof-edit-close"
          onClick={onClose}
          className="w-8 h-8 rounded-md border border-[#2c3122] grid place-items-center text-[#a9aa9a] hover:border-[#c9a86b] hover:text-[#c9a86b]"
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg>
        </button>
      </div>

      <div className="px-5 py-5 overflow-y-auto flex-1 space-y-7">
        {/* §A Basic */}
        <Section letter="A" title="Basic Info">
          <Field label="Type">
            <SelectInput
              data-testid="proof-field-kind"
              value={form.kind}
              onChange={(e) => set({ kind: e.target.value })}
              options={TYPES.map((t) => ({ value: t.id, label: t.label }))}
            />
          </Field>
          <Field
            label="Member name / Initials"
            hint='Shown on the card as the member badge — keep it short for privacy (e.g. "M.J.", "D.", "RJ from MIA").'
          >
            <TextInput
              data-testid="proof-field-name"
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
              placeholder='e.g. "D." or "RJ"'
            />
          </Field>
          <Field
            label="City / Region"
            hint='Shown under landed packages and on testimonials. Use city, state, or region only — never the full address.'
          >
            <TextInput
              data-testid="proof-field-region"
              value={form.region}
              onChange={(e) => set({ region: e.target.value })}
              placeholder='e.g. "Miami, FL" · "Brooklyn" · "Texas"'
            />
          </Field>
          <Field
            label="Headline (optional)"
            hint='Short headline for the testimonial card — leave blank to skip.'
          >
            <TextInput
              data-testid="proof-field-title"
              value={form.title}
              onChange={(e) => set({ title: e.target.value })}
              placeholder='e.g. "Touchdown · Batch 042" or leave blank'
            />
          </Field>
          <Field
            label="Their comment / quote"
            hint='What the member said — appears under their name. Emojis ok.'
          >
            <TextArea
              data-testid="proof-field-caption"
              value={form.caption}
              rows={3}
              onChange={(e) => set({ caption: e.target.value })}
              placeholder='e.g. "Mr.TD as usual ‼️ 🔥🔥" — or the gist of their feedback'
            />
          </Field>
          <ToggleRow data-testid="proof-toggle-visible" label="Visible on site" checked={form.visible} onChange={(v) => set({ visible: v })} />
        </Section>

        {/* §B Media */}
        <SectionMedia
          form={form} setForm={setForm}
          kindLabel={kindMeta.label}
          accept={kindMeta.accept}
        />

        {/* §C Live preview */}
        <SectionPreview form={form} kindMeta={kindMeta} />
      </div>

      <div className="px-5 py-4 border-t border-[#22261b] bg-[#10120c] flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[11px] font-mono tracking-[.06em] text-[#6e6f5f]">
          {lastSavedId ? <span className="text-[#5fa15a]">● Saved</span> : "Unsaved changes are discarded on close"}
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {lastSavedId && (
            <a
              data-testid="proof-view-on-site"
              href={liveSiteHref}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 px-3 py-2 rounded-md border border-[#c9a86b]/40 text-[#c9a86b] hover:bg-[#c9a86b]/[.08] text-[11px] tracking-[.2em] uppercase font-semibold"
            >
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"><path d="M14 3h7v7M21 3l-9 9M19 14v6a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h6"/></svg>
              View on Live Site
            </a>
          )}
          <button data-testid="proof-cancel" onClick={onClose} className="px-4 py-2.5 rounded-md border border-[#2c3122] text-[#a9aa9a] hover:border-[#c9a86b] text-[11px] tracking-[.2em] uppercase">Cancel</button>
          <button data-testid="proof-save-and-next" onClick={onSaveAndNext} disabled={saving} className="px-4 py-2.5 rounded-md border border-[#c9a86b]/50 text-[#c9a86b] hover:bg-[#c9a86b]/[.08] disabled:opacity-50 text-[11px] tracking-[.2em] uppercase font-semibold">Save &amp; Next</button>
          <button data-testid="proof-save" onClick={onSave} disabled={saving} className="px-5 py-2.5 rounded-md text-[12px] tracking-[.2em] uppercase font-bold disabled:opacity-50" style={{ background: "linear-gradient(180deg,#d4b675,#a07c39)", color: "#1a1407" }}>{saving ? "Saving…" : "Save Proof"}</button>
        </div>
      </div>
    </aside>
  );
}

/* ============================== Section B — Media ============================== */
function SectionMedia({ form, setForm, kindLabel, accept }) {
  const inputRef = React.useRef(null);
  const [progress, setProgress] = React.useState(null);
  const [err, setErr] = React.useState("");
  const isVideo = isVideoUrl(form.img);

  async function handle(file) {
    if (!file) return;
    setErr("");
    if (accept === "image" && !file.type.startsWith("image/")) { setErr("Please upload an image."); return; }
    if (accept === "video" && !file.type.startsWith("video/")) { setErr("Please upload a video."); return; }
    if (file.size > 200 * 1024 * 1024) { setErr("Too large (max 200 MB)."); return; }
    setProgress(0);
    try {
      const meta = await api.uploadMedia(file, (pct) => setProgress(pct));
      const url = meta.web_url || meta.url || meta.url_original;
      setForm((f) => ({ ...f, img: url, video_poster: meta.poster_url || f.video_poster }));
      setProgress(null);
    } catch (e) {
      setProgress(null);
      const msg = e.response?.data?.detail || "Upload failed";
      setErr(msg);
      toast(msg, "error");
    }
  }

  return (
    <Section letter="B" title={`Media · ${kindLabel}`}>
      {form.img ? (
        <div className="rounded-xl overflow-hidden border border-[#2c3122] bg-[#0a0c08]">
          <SmartMedia
            src={form.img}
            poster={form.video_poster ? mediaUrl(form.video_poster) : ""}
            alt="proof media"
            aspect="4/3"
            videoBadge={isVideo}
            showPlay={isVideo}
            rounded=""
          />
          <div className="flex items-center gap-2 p-2.5 border-t border-[#22261b]">
            <button
              data-testid="proof-media-replace"
              onClick={() => inputRef.current?.click()}
              className="px-3 py-1.5 rounded-md border border-[#c9a86b]/40 text-[#c9a86b] hover:bg-[#c9a86b]/[.08] text-[10.5px] tracking-[.2em] uppercase font-semibold"
            >Replace</button>
            <button
              data-testid="proof-media-clear"
              onClick={() => setForm((f) => ({ ...f, img: "", video_poster: "" }))}
              className="px-3 py-1.5 rounded-md border border-red-500/40 text-red-300 hover:bg-red-500/10 text-[10.5px] tracking-[.2em] uppercase"
            >Clear</button>
          </div>
        </div>
      ) : (
        <div
          data-testid="proof-media-dropzone"
          onClick={() => inputRef.current?.click()}
          onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { e.preventDefault(); handle(e.dataTransfer.files?.[0]); }}
          className="rounded-xl border-[1.5px] border-dashed border-[#2c3122] hover:border-[#c9a86b] cursor-pointer text-center py-8 px-4 text-[12.5px] text-[#6e6f5f] hover:text-[#c9a86b] transition"
          style={{ background: "repeating-linear-gradient(135deg,#0e110a,#0e110a 8px,#10130c 8px,#10130c 16px)" }}
        >
          <strong className="text-[#e8e4d8]">Drop {accept} here</strong> or click to browse · max 200 MB
          {accept === "video" && <div className="text-[11px] mt-1">auto-compressed to 1080p ~6 MB</div>}
        </div>
      )}
      <input
        ref={inputRef} type="file"
        accept={accept === "video" ? "video/*" : accept === "image" ? "image/*" : "image/*,video/*"}
        className="hidden"
        onChange={(e) => { handle(e.target.files?.[0]); e.target.value = ""; }}
      />
      {progress !== null && (
        <div className="mt-2 flex items-center gap-3">
          <div className="flex-1 h-[5px] bg-[#191c14] rounded-full overflow-hidden">
            <div className="h-full" style={{ width: `${progress}%`, background: "linear-gradient(90deg,#c9a86b,#e3c98c)" }} />
          </div>
          <span className="text-[10.5px] font-mono text-[#c9a86b]">{progress}%</span>
        </div>
      )}
      <div className="mt-2.5">
        <input
          type="url"
          data-testid="proof-media-url"
          placeholder="Or paste URL (https://…)"
          value={form.img || ""}
          onChange={(e) => setForm((f) => ({ ...f, img: e.target.value }))}
          className="w-full px-3 py-2 rounded-md bg-[#0e110a] border border-[#22261b] text-[12px] focus:outline-none focus:border-[#c9a86b]"
        />
      </div>
      {err && <div className="mt-2 text-[11px] text-red-300 bg-red-500/10 border border-red-500/25 rounded-md px-3 py-2">{err}</div>}
    </Section>
  );
}

/* ============================== Section C — Live Preview ============================== */
function SectionPreview({ form, kindMeta }) {
  const isVideo = isVideoUrl(form.img);
  const mediaSrc = form.img ? (isVideo ? (form.video_poster ? mediaUrl(form.video_poster) : null) : mediaUrl(form.img)) : null;

  return (
    <Section letter="C" title="Live Preview">
      <div className="rounded-2xl border border-[#2c3122] bg-[#0a0c08] overflow-hidden">
        <div className="px-3.5 py-2.5 border-b border-[#22261b] bg-[#10120c] flex items-center justify-between text-[10.5px] tracking-[.18em] uppercase text-[#6e6f5f]">
          <span>How it appears on 2high.com</span>
          <span className="text-[#5fa15a]">● Live render</span>
        </div>
        <div className="p-4">
          <SmartMedia
            src={form.img || ""}
            poster={form.video_poster ? mediaUrl(form.video_poster) : ""}
            alt={form.title || "proof"}
            aspect="4/3"
            badge={kindMeta.label}
            videoBadge={isVideo}
            showPlay={isVideo && !!form.img}
          >
            {!form.img && (
              <div className="absolute inset-0 grid place-items-center text-[#6e6f5f] text-[12px]">No media yet</div>
            )}
          </SmartMedia>
          {(form.name || form.region) && (
            <div className="text-[12px] text-[#c9a86b] mt-3 tracking-[.04em] uppercase font-mono">
              {form.name || "—"}{form.region ? ` · ${form.region}` : ""}
            </div>
          )}
          {form.title && <div className="text-[15px] font-semibold mt-2 leading-tight">{form.title}</div>}
          {form.caption && <p className="text-[12.5px] text-[#a9aa9a] mt-2 leading-relaxed">"{form.caption}"</p>}
          {!form.title && !form.caption && !form.name && !form.region && <div className="text-[12px] text-[#6e6f5f] mt-3">Add a member name, city, or caption to preview.</div>}
        </div>
      </div>
    </Section>
  );
}

/* ============================== bits ============================== */
function RowThumb({ src, alt }) {
  const [fit, setFit] = React.useState("cover");
  return (
    <img
      src={src}
      alt={alt}
      loading="lazy"
      onLoad={(e) => {
        const r = e.currentTarget.naturalWidth / e.currentTarget.naturalHeight;
        setFit(r >= 1.05 ? "cover" : "contain");
      }}
      className="absolute inset-0 w-full h-full"
      style={{ objectFit: fit, objectPosition: "center" }}
    />
  );
}

function Section({ letter, title, children }) {
  return (
    <section className="pb-6 border-b border-[#22261b] last:border-b-0 last:pb-0 space-y-3">
      <div className="flex items-center gap-2.5">
        <span className="w-[22px] h-[22px] rounded-md grid place-items-center text-[12px] font-bold" style={{ background: "#c9a86b", color: "#1a1407" }}>{letter}</span>
        <h3 className="text-[12.5px] tracking-[.16em] uppercase text-[#a9aa9a] m-0">{title}</h3>
      </div>
      {children}
    </section>
  );
}
function Field({ label, hint, children }) {
  return (
    <label className="block">
      <span className="block text-[10.5px] tracking-[.16em] uppercase text-[#6e6f5f] mb-1.5">{label}</span>
      {children}
      {hint && (
        <span className="block text-[10.5px] text-[#7a7c66] mt-1.5 leading-[1.5] normal-case">
          {hint}
        </span>
      )}
    </label>
  );
}
function ToggleRow({ label, checked, onChange, "data-testid": testid }) {
  return (
    <label data-testid={testid} className="flex items-center gap-2 cursor-pointer">
      <span className={`relative w-9 h-5 rounded-full border transition ${checked ? "bg-[#2d3a23] border-[#5fa15a]" : "bg-[#1f2218] border-[#2c3122]"}`}>
        <span className={`absolute top-[1px] w-3.5 h-3.5 rounded-full transition-all ${checked ? "left-[18px] bg-[#5fa15a]" : "left-[2px] bg-[#6e6f5f]"}`} />
      </span>
      <input type="checkbox" checked={!!checked} onChange={(e) => onChange(e.target.checked)} className="hidden" />
      <span className="text-[12px] text-[#a9aa9a]">{label}</span>
    </label>
  );
}
